import type { Uid } from "@/helpers/utility/Id";
import { set } from "lodash-es";
import { defineStore } from "pinia";

export type PresenceState = {
  activeSceneId: number,
  activeSceneLayers: Record<number, Uid<"sceneNode">>,
  closedSceneNodes: Uid<"sceneNode">[],
  footerSceneTitleVisible?: boolean,
  sceneTransforms?: Record<number, {
    translateX: number;
    translateY: number;
    scale: number;
  }>;
};

export type PresenceSnapshot = PresenceState;

export const createDefaultPresenceState = (): PresenceState => ({
  activeSceneId: 0,
  activeSceneLayers: {} as Record<number, Uid<"sceneNode">>,
  closedSceneNodes: [],
  sceneTransforms: {}
});

type PresenceRecordKey = "activeSceneLayers" | "sceneTransforms";
type PresenceStateRecords = {
  [K in PresenceRecordKey]: NonNullable<PresenceState[K]> extends Record<any, any> ? NonNullable<PresenceState[K]> : never
}

export const usePresenceStore = defineStore("editor/presence", {
  state: () => {
    const presence: PresenceState = createDefaultPresenceState();
    return presence;
  },
  actions: {
    set<K extends keyof PresenceState>(key: K, value: PresenceState[K]) {
      set(this, key, value);
    },
    setActiveLayer(layerUid: Uid<"sceneNode">) {
      this.activeSceneLayers[this.activeSceneId] = layerUid;
    },
    setRecordEntry<K extends PresenceRecordKey, U extends keyof PresenceStateRecords[K]>(
      recordKey: K,
      entryKey: U,
      entryValue: PresenceStateRecords[K][U]
    ) {
      if (!(recordKey in this)) {
        (this as any)[recordKey] = {};
      }
      (this as any)[recordKey][entryKey] = entryValue;
    },
    getSnapshot(): PresenceSnapshot {
      return {
        activeSceneId: this.activeSceneId,
        activeSceneLayers: { ...this.activeSceneLayers },
        closedSceneNodes: [...this.closedSceneNodes],
        footerSceneTitleVisible: this.footerSceneTitleVisible,
        sceneTransforms: this.sceneTransforms == null
          ? {}
          : Object.fromEntries(
            Object.entries(this.sceneTransforms).map(([sceneId, transform]) => [
              sceneId,
              { ...transform }
            ])
          )
      };
    },
    importSnapshot(snapshot?: Partial<PresenceSnapshot> | null) {
      const defaultState = createDefaultPresenceState();
      this.activeSceneId = snapshot?.activeSceneId ?? defaultState.activeSceneId;
      this.activeSceneLayers = { ...(snapshot?.activeSceneLayers ?? defaultState.activeSceneLayers) };
      this.closedSceneNodes = [...(snapshot?.closedSceneNodes ?? defaultState.closedSceneNodes)];
      this.sceneTransforms = {
        ...(snapshot?.sceneTransforms ?? defaultState.sceneTransforms)
      };
      this.footerSceneTitleVisible = snapshot?.footerSceneTitleVisible;
    },
    resetPresence() {
      this.importSnapshot(createDefaultPresenceState());
      this.footerSceneTitleVisible = undefined;
    }
  }
});
